import './style.css';
import './fortune.css';
import {
  formatBirthDigits,
  generateFortune,
  getKstDateKey,
  validateBirthDate,
  type FortuneResult,
} from './fortune-engine';
import { downloadFortuneCard, shareFortuneCard } from './fortune-share';
import { KAKAO_JAVASCRIPT_KEY, preloadKakaoShare } from './kakao-share';
import { relatedToolsMarkup, siteBrandMarkup } from './site-chrome';

type FortunePhase = 'input' | 'opening' | 'result';

const STORAGE_KEY = 'oneultool-fortune-birth-v1';

const app = document.querySelector<HTMLDivElement>('#app');
if (!app) throw new Error('운세 화면을 찾지 못했습니다.');

document.documentElement.classList.add('fortune-page');
document.title = '오늘의 한입 운세 | 오늘툴';
preloadKakaoShare();

let phase: FortunePhase = 'input';
let birthDigits = readRememberedBirth();
let rememberBirth = birthDigits.length === 8;
let inputError = '';
let fortune: FortuneResult | null = null;
let toastTimer = 0;

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  })[character]!);
}

function readRememberedBirth(): string {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) ?? '';
    return validateBirthDate(saved).ok ? saved.replace(/\D/g, '') : '';
  } catch {
    return '';
  }
}

function rememberBirthOnDevice(): void {
  try {
    if (rememberBirth) localStorage.setItem(STORAGE_KEY, birthDigits);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    showToast('브라우저 설정 때문에 생일을 기억하지 못했어요.');
  }
}

function cookieMarkup(modifier = '', paperText = ''): string {
  const isSplitting = modifier.includes('is-splitting');
  const paperCopy = escapeHtml(paperText || '오늘의 한마디');
  return `
    <div class="fortune-cookie ${modifier}" aria-hidden="true">
      <span class="fortune-cookie-shadow"></span>
      ${isSplitting ? `<span class="fortune-cookie-paper">${paperCopy}</span>` : ''}
      <svg class="fortune-cookie-art fortune-cookie-closed" viewBox="0 0 240 180" focusable="false">
        <defs>
          <linearGradient id="fortune-cookie-body-closed" x1=".2" y1="0" x2=".4" y2="1">
            <stop offset="0" stop-color="#fbde9e" />
            <stop offset=".42" stop-color="#f2c169" />
            <stop offset="1" stop-color="#db9124" />
          </linearGradient>
          <linearGradient id="fortune-cookie-rim-closed" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stop-color="#e3b15a" />
            <stop offset="1" stop-color="#b87113" />
          </linearGradient>
          <filter id="fortune-cookie-soft-closed" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="6" />
          </filter>
        </defs>
        <path d="M120 164C92 163 52 142 32 112C24 100 20 94 22 92C30 66 54 56 74 62C92 67 110 71 120 78C130 71 148 67 166 62C186 56 210 66 218 92C220 94 216 100 208 112C188 142 148 163 120 164Z" fill="url(#fortune-cookie-body-closed)" stroke="url(#fortune-cookie-rim-closed)" stroke-width="2.2" />
        <path d="M36 100C64 126 92 136 120 136C148 136 176 126 204 100C208 106 208 109 208 112C188 142 148 163 120 164C92 163 52 142 32 112C32 109 32 106 36 100Z" fill="rgba(150,88,14,.14)" />
        <ellipse cx="76" cy="80" rx="27" ry="11" transform="rotate(-15 76 80)" fill="rgba(255,252,240,.55)" filter="url(#fortune-cookie-soft-closed)" />
        <ellipse cx="164" cy="80" rx="27" ry="11" transform="rotate(15 164 80)" fill="rgba(255,252,240,.55)" filter="url(#fortune-cookie-soft-closed)" />
        <path d="M36 100C64 126 92 136 120 136C148 136 176 126 204 100" stroke="rgba(146,86,12,.42)" stroke-width="2.6" stroke-linecap="round" fill="none" />
        <path d="M37 96C65 122 92 132 120 132C148 132 175 122 203 96" stroke="rgba(255,247,224,.5)" stroke-width="1.8" stroke-linecap="round" fill="none" />
        <path class="fortune-cookie-crease" d="M120 79C121.4 97 121.4 118 120 134" stroke="rgba(146,86,12,.34)" stroke-width="3" stroke-linecap="round" fill="none" />
        <path d="M118 80C116.6 98 116.6 118 118 132" stroke="rgba(255,247,224,.42)" stroke-width="1.5" stroke-linecap="round" fill="none" />
      </svg>
      ${isSplitting ? `
        <svg class="fortune-cookie-art fortune-cookie-piece fortune-cookie-left" viewBox="0 0 240 180" focusable="false">
          <defs>
            <linearGradient id="fortune-cookie-body-left" x1=".2" y1="0" x2=".4" y2="1"><stop offset="0" stop-color="#fbde9e" /><stop offset=".42" stop-color="#f2c169" /><stop offset="1" stop-color="#db9124" /></linearGradient>
            <linearGradient id="fortune-cookie-rim-left" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#e3b15a" /><stop offset="1" stop-color="#b87113" /></linearGradient>
            <linearGradient id="fortune-cookie-break-left" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#f6dfb0" /><stop offset="1" stop-color="#d9b270" /></linearGradient>
            <filter id="fortune-cookie-soft-left" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="6" /></filter>
          </defs>
          <path d="M120 78C110 71 92 67 74 62C54 56 30 66 22 92C20 94 24 100 32 112C52 142 92 163 120 164L112 152L121 141L111 130L120 119L110 108L119 97L111 87Z" fill="url(#fortune-cookie-body-left)" stroke="url(#fortune-cookie-rim-left)" stroke-width="2.2" />
          <path d="M36 100C64 126 92 136 118 136L120 164C92 163 52 142 32 112C32 109 32 106 36 100Z" fill="rgba(150,88,14,.14)" />
          <ellipse cx="76" cy="80" rx="27" ry="11" transform="rotate(-15 76 80)" fill="rgba(255,252,240,.55)" filter="url(#fortune-cookie-soft-left)" />
          <path d="M36 100C64 126 92 136 118 136" stroke="rgba(146,86,12,.42)" stroke-width="2.6" stroke-linecap="round" fill="none" />
          <path d="M120 164L112 152L121 141L111 130L120 119L110 108L119 97L111 87L120 78" fill="none" stroke="url(#fortune-cookie-break-left)" stroke-width="2.8" stroke-linejoin="round" stroke-linecap="round" />
        </svg>
        <svg class="fortune-cookie-art fortune-cookie-piece fortune-cookie-right" viewBox="0 0 240 180" focusable="false">
          <defs>
            <linearGradient id="fortune-cookie-body-right" x1=".2" y1="0" x2=".4" y2="1"><stop offset="0" stop-color="#fbde9e" /><stop offset=".42" stop-color="#f2c169" /><stop offset="1" stop-color="#db9124" /></linearGradient>
            <linearGradient id="fortune-cookie-rim-right" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#e3b15a" /><stop offset="1" stop-color="#b87113" /></linearGradient>
            <linearGradient id="fortune-cookie-break-right" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#f6dfb0" /><stop offset="1" stop-color="#d9b270" /></linearGradient>
            <filter id="fortune-cookie-soft-right" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="6" /></filter>
          </defs>
          <g transform="translate(240,0) scale(-1,1)">
            <path d="M120 78C110 71 92 67 74 62C54 56 30 66 22 92C20 94 24 100 32 112C52 142 92 163 120 164L112 152L121 141L111 130L120 119L110 108L119 97L111 87Z" fill="url(#fortune-cookie-body-right)" stroke="url(#fortune-cookie-rim-right)" stroke-width="2.2" />
            <path d="M36 100C64 126 92 136 118 136L120 164C92 163 52 142 32 112C32 109 32 106 36 100Z" fill="rgba(150,88,14,.14)" />
            <ellipse cx="76" cy="80" rx="27" ry="11" transform="rotate(-15 76 80)" fill="rgba(255,252,240,.55)" filter="url(#fortune-cookie-soft-right)" />
            <path d="M36 100C64 126 92 136 118 136" stroke="rgba(146,86,12,.42)" stroke-width="2.6" stroke-linecap="round" fill="none" />
            <path d="M120 164L112 152L121 141L111 130L120 119L110 108L119 97L111 87L120 78" fill="none" stroke="url(#fortune-cookie-break-right)" stroke-width="2.8" stroke-linejoin="round" stroke-linecap="round" />
          </g>
        </svg>
        ${Array.from({ length: 8 }, (_, index) => `<i class="fortune-cookie-crumb crumb-${index + 1}"></i>`).join('')}
      ` : ''}
    </div>`;
}

function footerMarkup(): string {
  return `
    <footer class="legal-footer">
      <a href="/privacy/">개인정보처리방침</a>
      <a href="/terms/">이용약관</a>
      <span>© 2026 오늘툴</span>
    </footer>`;
}

app.innerHTML = `
  <div class="page-shell fortune-shell">
    <div class="fortune-glow fortune-glow-one" aria-hidden="true"></div>
    <div class="fortune-glow fortune-glow-two" aria-hidden="true"></div>
    <div class="fortune-glow fortune-glow-three" aria-hidden="true"></div>
    <header class="site-brand-header fortune-header">${siteBrandMarkup()}</header>
    <main class="fortune-main">
      <section class="fortune-intro" aria-labelledby="fortune-title">
        <span>오늘의 작은 재미</span>
        <h1 id="fortune-title">오늘의 한입 운세</h1>
        <p>생일을 알려주면 오늘 당신에게 필요한 한마디를 포춘쿠키에 담아드려요.</p>
      </section>
      <div id="fortune-cookie-host" class="fortune-cookie-host">${cookieMarkup()}</div>
      <div id="fortune-stage" aria-live="polite"></div>
      <p class="fortune-disclaimer">생년월일을 씨앗으로 만든 오늘의 재미 운세예요. 가볍게 즐겨주세요.</p>
      ${relatedToolsMarkup('today-fortune')}
    </main>
    ${footerMarkup()}
    <div id="fortune-toast" class="toast" role="status" aria-live="polite"></div>
  </div>`;

const stageElement = document.querySelector<HTMLDivElement>('#fortune-stage');
const cookieHostElement = document.querySelector<HTMLDivElement>('#fortune-cookie-host');
if (!stageElement || !cookieHostElement) throw new Error('운세 구성 요소를 찾지 못했습니다.');
const stage: HTMLDivElement = stageElement;
const cookieHost: HTMLDivElement = cookieHostElement;

function inputMarkup(): string {
  return `
    <section class="fortune-card fortune-input-card" aria-label="생년월일 입력">
      <form id="fortune-input-form" class="fortune-input-form" novalidate>
        <label for="fortune-birth">생년월일</label>
        <div class="fortune-birth-field ${inputError ? 'invalid' : ''}">
          <input
            id="fortune-birth"
            type="text"
            inputmode="numeric"
            enterkeyhint="done"
            autocomplete="bday"
            aria-describedby="fortune-birth-error"
            aria-invalid="${inputError ? 'true' : 'false'}"
            placeholder="1992. 07. 14"
            maxlength="12"
            value="${formatBirthDigits(birthDigits)}"
          />
        </div>
        <p id="fortune-birth-error" class="fortune-input-error" ${inputError ? 'role="alert"' : ''}>${escapeHtml(inputError)}</p>
        <label class="fortune-remember">
          <input id="fortune-remember" type="checkbox" ${rememberBirth ? 'checked' : ''} />
          <span>이 기기에 생일 기억하기</span>
        </label>
        <button id="fortune-open" class="button button-primary" type="submit" ${birthDigits.length === 8 ? '' : 'disabled'}>오늘 운세 열어보기</button>
      </form>
    </section>`;
}

function openingMarkup(): string {
  return `
    <section class="fortune-card fortune-opening-card" aria-labelledby="fortune-opening-title">
      <span class="fortune-opening-dot" aria-hidden="true"></span>
      <h2 id="fortune-opening-title">포춘쿠키를 여는 중이에요</h2>
      <p>오늘의 한마디를 살짝 꺼내볼게요.</p>
    </section>`;
}

function scoreMarkup(result: FortuneResult): string {
  return result.scores.map((score) => `
    <article class="fortune-score-item">
      <span class="fortune-score-icon" aria-hidden="true">${score.emoji}</span>
      <strong>${score.label}</strong>
      <b>${score.value}</b>
      <span class="fortune-score-track" aria-hidden="true"><i style="width: ${score.value}%"></i></span>
      <small>${score.note}</small>
    </article>`).join('');
}

function resultMarkup(result: FortuneResult): string {
  return `
    <section class="fortune-card fortune-result-card" aria-labelledby="fortune-result-title">
      <header class="fortune-result-header">
        <span>${result.dateLabel}</span>
        <strong>오늘의 한입 운세</strong>
      </header>
      <div class="fortune-overall" style="--fortune-score: ${result.overall}%">
        <div class="fortune-overall-inner">
          <span>오늘의 점수</span>
          <strong>${result.overall}</strong>
          <small>/ 100</small>
        </div>
      </div>
      <div class="fortune-message">
        <h2 id="fortune-result-title">${result.headline}</h2>
        <p>${result.message}</p>
      </div>
      <div class="fortune-score-grid" aria-label="분야별 운세">
        ${scoreMarkup(result)}
      </div>
      <div class="fortune-detail-grid">
        <article class="fortune-detail fortune-detail-wide">
          <span class="fortune-detail-emoji" aria-hidden="true">✓</span>
          <div><small>오늘 해볼 일</small><strong>${result.todo}</strong></div>
        </article>
        <article class="fortune-detail">
          <span class="fortune-color-dot" style="background: ${result.luckyColor.hex}" aria-hidden="true"></span>
          <div><small>행운의 색</small><strong>${result.luckyColor.name}</strong></div>
        </article>
        <article class="fortune-detail">
          <span class="fortune-detail-emoji" aria-hidden="true">#</span>
          <div><small>행운의 숫자</small><strong>${result.luckyNumber}</strong></div>
        </article>
        <article class="fortune-detail">
          <span class="fortune-detail-emoji" aria-hidden="true">↗</span>
          <div><small>행운의 방향</small><strong>${result.direction}</strong></div>
        </article>
      </div>
      <div class="fortune-actions">
        <button id="fortune-save" class="button button-soft" type="button"><span aria-hidden="true">↓</span> 이미지로 저장</button>
        <button id="fortune-share" class="button fortune-kakao-button" type="button"><span class="fortune-kakao-mark" aria-hidden="true">K</span> 카카오톡 카드로 공유</button>
      </div>
      <button id="fortune-reset" class="fortune-reset-button" type="button">생년월일 다시 입력하기</button>
      <p class="fortune-next-note">운세는 한국 시간 기준으로 매일 자정에 새로 열려요.</p>
    </section>`;
}

function showToast(message: string): void {
  const toast = document.querySelector<HTMLElement>('#fortune-toast');
  if (!toast) return;
  window.clearTimeout(toastTimer);
  toast.textContent = message;
  toast.classList.add('show');
  toastTimer = window.setTimeout(() => toast.classList.remove('show'), 3_000);
}

function setBusy(button: HTMLButtonElement, busy: boolean, busyLabel: string): void {
  if (!button.dataset.label) button.dataset.label = button.innerHTML;
  button.disabled = busy;
  button.setAttribute('aria-busy', String(busy));
  if (busy) button.textContent = busyLabel;
  else button.innerHTML = button.dataset.label;
}

function bindInput(): void {
  const form = document.querySelector<HTMLFormElement>('#fortune-input-form');
  const input = document.querySelector<HTMLInputElement>('#fortune-birth');
  const remember = document.querySelector<HTMLInputElement>('#fortune-remember');
  const submit = document.querySelector<HTMLButtonElement>('#fortune-open');
  const error = document.querySelector<HTMLElement>('#fortune-birth-error');
  const field = document.querySelector<HTMLElement>('.fortune-birth-field');
  if (!form || !input || !remember || !submit || !error || !field) return;

  input.addEventListener('input', () => {
    birthDigits = input.value.replace(/\D/g, '').slice(0, 8);
    input.value = formatBirthDigits(birthDigits);
    input.setSelectionRange(input.value.length, input.value.length);
    submit.disabled = birthDigits.length !== 8;
    if (inputError) {
      inputError = '';
      error.textContent = '';
      error.removeAttribute('role');
      input.setAttribute('aria-invalid', 'false');
      field.classList.remove('invalid');
    }
  });

  remember.addEventListener('change', () => {
    rememberBirth = remember.checked;
    if (!rememberBirth) rememberBirthOnDevice();
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const validation = validateBirthDate(birthDigits);
    if (!validation.ok) {
      inputError = validation.error;
      error.textContent = inputError;
      error.setAttribute('role', 'alert');
      input.setAttribute('aria-invalid', 'true');
      field.classList.add('invalid');
      input.focus();
      return;
    }

    rememberBirth = remember.checked;
    rememberBirthOnDevice();
    fortune = generateFortune(validation.value.digits, getKstDateKey());
    phase = 'opening';
    renderStage();
    const delay = matchMedia('(prefers-reduced-motion: reduce)').matches ? 160 : 1_300;
    window.setTimeout(() => {
      phase = 'result';
      renderStage();
      stage.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    }, delay);
  });
}

function bindResult(result: FortuneResult): void {
  const save = document.querySelector<HTMLButtonElement>('#fortune-save');
  const share = document.querySelector<HTMLButtonElement>('#fortune-share');
  const reset = document.querySelector<HTMLButtonElement>('#fortune-reset');

  save?.addEventListener('click', async () => {
    setBusy(save, true, '이미지 만드는 중…');
    try {
      const mode = await downloadFortuneCard(result, save);
      if (mode === 'download') showToast('이미지 다운로드를 요청했어요. 다운로드 목록을 확인해 주세요.');
    } catch (error) {
      console.error(error);
      showToast('이미지를 만들지 못했어요. 잠시 후 다시 시도해 주세요.');
    } finally {
      setBusy(save, false, '');
    }
  });

  share?.addEventListener('click', async () => {
    setBusy(share, true, '공유 카드 만드는 중…');
    let mode: Awaited<ReturnType<typeof shareFortuneCard>> | null = null;
    try {
      mode = await shareFortuneCard(result, KAKAO_JAVASCRIPT_KEY);
    } catch (error) {
      console.error(error);
      showToast(error instanceof Error ? error.message : '공유 카드를 만들지 못했어요.');
    } finally {
      setBusy(share, false, '');
    }

    if (mode === 'kakao-ready') {
      const readyLabel = '<span class="fortune-kakao-mark" aria-hidden="true">K</span> 준비 완료 · 카카오톡 열기';
      share.dataset.label = readyLabel;
      share.innerHTML = readyLabel;
      showToast('카드 준비가 끝났어요. 버튼을 한 번 더 눌러 카카오톡을 열어주세요.');
    }
    if (mode === 'kakao') showToast('카카오톡 공유 화면을 열었어요.');
  });

  reset?.addEventListener('click', () => {
    phase = 'input';
    fortune = null;
    inputError = '';
    renderStage();
    document.querySelector<HTMLInputElement>('#fortune-birth')?.focus();
    stage.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
}

function renderStage(): void {
  if (phase === 'input') {
    cookieHost.className = 'fortune-cookie-host';
    cookieHost.innerHTML = cookieMarkup();
    stage.innerHTML = inputMarkup();
    bindInput();
    return;
  }
  if (phase === 'opening') {
    cookieHost.className = 'fortune-cookie-host is-opening';
    cookieHost.innerHTML = cookieMarkup('is-splitting', fortune?.headline);
    stage.innerHTML = openingMarkup();
    return;
  }
  if (!fortune) return;
  cookieHost.className = 'fortune-cookie-host is-result';
  cookieHost.innerHTML = '';
  stage.innerHTML = resultMarkup(fortune);
  bindResult(fortune);
}

renderStage();
